let currentSessionToken = localStorage.getItem('dataunfold_session_token') || '';

export const setApiSessionToken = (token) => {
  currentSessionToken = String(token || '').trim();
  try {
    if (currentSessionToken) {
      localStorage.setItem('dataunfold_session_token', currentSessionToken);
    } else {
      localStorage.removeItem('dataunfold_session_token');
    }
  } catch (error) {
    // Ignore storage failures and keep the in-memory token available.
  }
};

export const clearApiSessionToken = () => {
  currentSessionToken = '';
  try {
    localStorage.removeItem('dataunfold_session_token');
  } catch (error) {
    // Ignore storage failures.
  }
};

export const getApiSessionToken = () => {
  const storedToken = currentSessionToken || localStorage.getItem('dataunfold_session_token') || '';
  currentSessionToken = storedToken;
  return storedToken;
};

const getWebServerBaseUrlCandidates = () => {
  const configuredBaseUrl = process.env.REACT_APP_WEBSERVER_URL?.trim();
  if (configuredBaseUrl) {
    return [configuredBaseUrl.replace(/\/+$/, '')];
  }

  if (typeof window === 'undefined') {
    return [''];
  }

  const hostname = String(window.location.hostname || '').toLowerCase();
  const origin = String(window.location.origin || '').replace(/\/+$/, '');
  const candidates = [];

  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
  const isLanBackendHost = /^192\.168\.10\.\d{1,3}$/.test(hostname);

  if (isLocalhost || isLanBackendHost) {
    candidates.push(`http://${hostname}:5000`);
  }

  if (origin) {
    candidates.push(origin);
  }

  return [...new Set(candidates.filter(Boolean))];
};

const createRequestHeaders = (options = {}) => {
  const headers = new Headers(options.headers || {});
  const sessionToken = getApiSessionToken();
  if (sessionToken) {
    headers.set('X-Session-Token', sessionToken);
  }
  if (options.body && !(options.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  return headers;
};

const parseResponsePayload = async (response) => {
  const responseText = await response.text().catch(() => '');
  if (!responseText) {
    return {};
  }

  try {
    return JSON.parse(responseText);
  } catch (error) {
    const normalizedText = responseText.trim();
    const looksLikeHtml = /^<!doctype html>/i.test(normalizedText) || /^<html[\s>]/i.test(normalizedText);
    const looksLikeExpressRouteError = /Cannot\s+(GET|POST|PUT|DELETE|PATCH)\s+/i.test(normalizedText);
    return {
      message: responseText.slice(0, 300),
      response_type: looksLikeHtml ? 'html' : 'text',
      response_hint: looksLikeExpressRouteError ? 'route_not_found_or_wrong_server' : undefined,
    };
  }
};

const buildApiErrorMessage = (response, payload) => {
  const statusCode = response.status;
  const errorCode = payload?.error?.code || payload?.code || '';
  const message = payload?.error?.message || payload?.message || payload?.error || response.statusText || 'Request failed';
  const statusLabel = Number.isInteger(statusCode) ? String(statusCode) : 'HTTP';
  const responseType = payload?.response_type || '';
  const responseHint = payload?.response_hint || '';

  if (responseType === 'html' && statusCode === 404) {
    return '404 route_not_found_or_wrong_server: The server returned HTML instead of JSON. Check that the frontend is calling the webserver API, not the React dev server.';
  }

  if (errorCode) {
    return `${statusLabel} ${errorCode}: ${message}`;
  }
  if (responseHint) {
    return `${statusLabel} ${responseHint}: ${message}`;
  }
  return `${statusLabel}: ${message}`;
};

export const apiRequest = async (path, options = {}) => {
  const candidates = getWebServerBaseUrlCandidates();
  let lastError = null;

  for (let index = 0; index < candidates.length; index += 1) {
    const baseUrl = candidates[index];
    const requestUrl = new URL(path, baseUrl || window.location.origin).toString();

    try {
      const response = await fetch(requestUrl, {
        credentials: 'same-origin',
        ...options,
        headers: createRequestHeaders(options)
      });

      const payload = await parseResponsePayload(response);
      if (response.ok) {
        return payload;
      }

      const errorMessage = buildApiErrorMessage(response, payload);
      lastError = new Error(errorMessage);

      const shouldRetryOnThisResponse = (
        payload?.response_hint === 'route_not_found_or_wrong_server'
        || payload?.response_type === 'html'
        || response.status === 404
        || response.status === 405
      );

      if (!shouldRetryOnThisResponse || index === candidates.length - 1) {
        throw lastError;
      }
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      if (index === candidates.length - 1) {
        throw lastError;
      }
    }
  }

  throw lastError || new Error('Request failed');
};

export const apiBinaryRequest = async (path, options = {}) => {
  const candidates = getWebServerBaseUrlCandidates();
  let lastError = null;

  for (let index = 0; index < candidates.length; index += 1) {
    const baseUrl = candidates[index];
    const requestUrl = new URL(path, baseUrl || window.location.origin).toString();

    try {
      const response = await fetch(requestUrl, {
        credentials: 'same-origin',
        ...options,
        headers: createRequestHeaders(options)
      });

      if (response.ok) {
        return response;
      }

      const payload = await parseResponsePayload(response);
      const errorMessage = buildApiErrorMessage(response, payload);
      lastError = new Error(errorMessage);

      const shouldRetryOnThisResponse = (
        payload?.response_hint === 'route_not_found_or_wrong_server'
        || payload?.response_type === 'html'
        || response.status === 404
        || response.status === 405
      );

      if (!shouldRetryOnThisResponse || index === candidates.length - 1) {
        throw lastError;
      }
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      if (index === candidates.length - 1) {
        throw lastError;
      }
    }
  }

  throw lastError || new Error('Request failed');
};
